import React from "react";
import { Siren, Phone, Clock, ShieldCheck } from "lucide-react";
import { Seo } from "@/components/Seo";
import { useLead } from "@/context/LeadContext";
import { AnimatedLines } from "@/components/landing/AnimatedLines";
import { CONTACT } from "@/data/pages";

const CASES = [
  "Sinistre (dégât des eaux, incendie, salissures accidentelles)",
  "Vandalisme (tags, projections, dégradations)",
  "Retour de location LLD / LOA à échéance",
  "Restitution d'appartement (état des lieux, caution)",
  "Événement de dernière minute (vente, visite, réception)",
];

export default function Urgence() {
  const { openForm } = useLead();
  return (
    <div data-testid="urgence-page">
      <Seo
        title="Intervention d'urgence 24/48h — Nettoyage | MARTIN NETTOYAGE"
        description="Urgence nettoyage : sinistre, vandalisme, retour LLD/LOA, restitution d'appartement. Devis 24h, intervention 24/48h. Belfort, Montbéliard, Héricourt."
        path="/intervention-urgence"
      />
      <section className="relative overflow-hidden bg-alert text-white">
        <AnimatedLines variant="dark" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 py-16 lg:py-20">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold bg-white/15 px-4 py-2 rounded-sm">
            <Siren className="w-4 h-4" /> Urgences
          </div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-5 leading-[1.05] max-w-3xl">
            Une urgence ? Devis en 24h, intervention sous 24/48h
          </h1>
          <p className="text-white/90 mt-5 text-base sm:text-lg max-w-2xl">
            Nous gérons les situations de crise avec réactivité et flexibilité. Appelez-nous directement pour une prise en charge rapide.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={CONTACT.phoneHref}
              data-testid="urgence-call"
              className="cta inline-flex items-center gap-2 bg-white text-alert font-bold px-8 py-4 rounded-sm hover:bg-ink hover:text-white"
            >
              <Phone className="w-5 h-5" /> {CONTACT.phone}
            </a>
            <button
              data-testid="urgence-cta"
              onClick={() => openForm("urgence")}
              className="cta inline-flex items-center gap-2 border border-white/40 font-bold px-8 py-4 rounded-sm hover:bg-white hover:text-alert"
            >
              Demander un devis urgent
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight text-ink">Situations prises en charge</h2>
          <ul className="mt-6 space-y-3">
            {CASES.map((c) => (
              <li key={c} className="flex items-start gap-3 text-ink">
                <Siren className="w-5 h-5 text-alert mt-0.5 shrink-0" /> {c}
              </li>
            ))}
          </ul>
        </div>
        <div className="space-y-5">
          <div className="flex gap-4 bg-marble-light border border-border rounded-sm p-6">
            <Clock className="w-7 h-7 text-ink shrink-0" />
            <div>
              <h3 className="font-heading font-bold text-ink">Réactivité maximale</h3>
              <p className="text-muted-foreground mt-1 text-sm">Devis sous 24h pour les urgences, 48h pour les demandes classiques.</p>
            </div>
          </div>
          <div className="flex gap-4 bg-marble-light border border-border rounded-sm p-6">
            <ShieldCheck className="w-7 h-7 text-ink shrink-0" />
            <div>
              <h3 className="font-heading font-bold text-ink">Restitution sereine</h3>
              <p className="text-muted-foreground mt-1 text-sm">Retour de location et restitution d'appartement : caution rendue ou 2ᵉ passage offert.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
